<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import type { EntityTemplate } from '../types/entities';

const props = defineProps<{ templates: EntityTemplate[]; initialTitle: string }>();
const emit = defineEmits<{
  create: [payload: { templateId: string; title: string; fieldValues: Record<string, unknown> }, done: (entityId?: string) => void];
  cancel: [];
  created: [entityId: string];
}>();
const templateId = ref(props.templates[0]?.id ?? '');
const title = ref(props.initialTitle);
const values = reactive<Record<string, string>>({});
const busy = ref(false);
const template = computed(() => props.templates.find((item) => item.id === templateId.value));
const requiredFields = computed(() => (template.value?.fields ?? []).filter((field) => field.required && !['reference', 'reference-list', 'boolean'].includes(field.kind)));
watch(templateId, () => { for (const key of Object.keys(values)) delete values[key]; });

function submit() {
  if (!template.value || !title.value.trim() || busy.value) return;
  const fieldValues: Record<string, unknown> = { ...values };
  for (const field of requiredFields.value) if (field.kind === 'number') fieldValues[field.key] = Number(values[field.key]);
  busy.value = true;
  emit('create', { templateId: template.value.id, title: title.value.trim(), fieldValues }, (entityId) => {
    busy.value = false;
    if (entityId) { emit('cancel'); emit('created', entityId); }
  });
}
</script>

<template>
  <form class="quick-create" @submit.prevent="submit">
    <strong>Neue Entity anlegen</strong>
    <input v-model="title" autofocus required maxlength="240" placeholder="Name der neuen Entity" aria-label="Name der neuen Entity" />
    <select v-if="templates.length > 1" v-model="templateId" aria-label="Template für neue Entity">
      <option v-for="item in templates" :key="item.id" :value="item.id">{{ item.name }}</option>
    </select>
    <label v-for="field in requiredFields" :key="field.id">{{ field.label }}
      <textarea v-if="field.kind === 'long-text'" v-model="values[field.key]" required />
      <input v-else v-model="values[field.key]" :type="field.kind === 'number' ? 'number' : 'text'" required />
    </label>
    <div><button type="submit" :disabled="busy">{{ busy ? 'Erstellt …' : 'Erstellen ↵' }}</button><button type="button" :disabled="busy" @click="emit('cancel')">Abbrechen</button></div>
  </form>
</template>

<style scoped>
.quick-create{display:grid;gap:8px;padding:12px;border-top:1px solid var(--ui-border)}
.quick-create strong{font-size:12px}
.quick-create label{display:grid;gap:4px;font-size:11px;color:var(--ui-text-muted)}
.quick-create input,.quick-create select,.quick-create textarea{box-sizing:border-box;width:100%;border:1px solid var(--ui-border);border-radius:6px;background:var(--ui-surface-2);color:var(--ui-text);padding:8px;font:inherit}
.quick-create>div{display:flex;gap:6px}
.quick-create button{border:1px solid var(--ui-border);border-radius:6px;background:var(--ui-surface-3);color:var(--ui-text);padding:7px 10px;cursor:pointer}
</style>
